import { isApiError } from "@simpro/api-client";
import { keepPreviousData, queryOptions } from "@tanstack/react-query";
import { apiClient } from "@/lib/api";
import { branchSlug } from "@/lib/branch";
import {
  frequentServicesSchema,
  type MyAppointmentScope,
  myAppointmentItemSchema,
  myQueuePositionSchema,
} from "../schemas/myAppointment.schemas";

export const MY_APPOINTMENTS = "/api/v1/salon/me/appointments";

/** A screen of visits on a phone. The server's default is 25; ten keeps the page short. */
export const MY_APPOINTMENTS_PAGE_SIZE = 10;

/** How often the queue position is re-read while the customer waits (FR-SA-065). */
export const QUEUE_REFRESH_MS = 30_000;

export interface MyAppointmentsParams {
  scope: MyAppointmentScope;
  page: number;
  pageSize?: number;
}

export const myAppointmentKeys = {
  all: ["my-appointments"] as const,
  lists: () => [...myAppointmentKeys.all, "list"] as const,
  list: (params: MyAppointmentsParams) =>
    [
      ...myAppointmentKeys.lists(),
      {
        scope: params.scope,
        page: params.page,
        pageSize: params.pageSize ?? MY_APPOINTMENTS_PAGE_SIZE,
      },
    ] as const,
  queue: (id: string) => [...myAppointmentKeys.all, "queue", id] as const,
  frequentServices: () => [...myAppointmentKeys.all, "frequent-services"] as const,
};

export function visitPath(id: string, action?: string): string {
  const base = `${MY_APPOINTMENTS}/${encodeURIComponent(id)}`;
  return action ? `${base}/${action}` : base;
}

/**
 * The customer's own visits, upcoming (soonest first) or past (newest first), a page at a time
 * (TDD-SA FR-SA-052). The client id comes from the token, so nothing here names the customer.
 */
export function myAppointmentsQuery(params: MyAppointmentsParams) {
  return queryOptions({
    queryKey: myAppointmentKeys.list(params),
    queryFn: ({ signal }) =>
      apiClient.getPaginated(myAppointmentItemSchema, MY_APPOINTMENTS, {
        query: {
          scope: params.scope,
          page: params.page,
          pageSize: params.pageSize ?? MY_APPOINTMENTS_PAGE_SIZE,
        },
        signal,
      }),
    placeholderData: keepPreviousData,
  });
}

/**
 * Where the customer stands in the line for one visit (FR-SA-065). The server answers 404
 * `salon.queue_entry_not_found` when they are not in it (not checked in yet, or already in the
 * chair); that is a normal answer here, read as null rather than as a failure.
 */
export function myQueuePositionQuery(id: string) {
  return queryOptions({
    queryKey: myAppointmentKeys.queue(id),
    queryFn: async ({ signal }) => {
      try {
        return await apiClient.get(myQueuePositionSchema, visitPath(id, "queue"), { signal });
      } catch (error) {
        if (isApiError(error) && error.code === "salon.queue_entry_not_found") return null;
        throw error;
      }
    },
    refetchInterval: QUEUE_REFRESH_MS,
    staleTime: 0,
  });
}

/** What the customer books most, for "book again" in one tap (FR-SA-083). */
export function frequentServicesQuery() {
  return queryOptions({
    queryKey: myAppointmentKeys.frequentServices(),
    queryFn: ({ signal }) =>
      // At the branch the site is open on: its prices, and nothing it does not offer (FR-SA-241, FR-SA-243).
      apiClient.get(frequentServicesSchema, `${MY_APPOINTMENTS}/frequent-services`, { signal, query: { branch: branchSlug } }),
  });
}
